/**
 * A PHP project, as its `composer.json` and its layout describe it (§15) — the
 * install, the run, the check and the port a session prefills with.
 *
 * Composer's `scripts` are the repository's own word where it has one, the way
 * a `package.json`'s are; past that, the framework decides: Laravel serves
 * itself through `artisan`, and anything with a front controller — Symfony,
 * Slim, a plain `public/index.php` — runs on PHP's built-in server. Every
 * command binds `0.0.0.0` on the port it reports, since Preview publishes that
 * field and a server listening elsewhere is a mapping that never answers.
 */
import { readJson, type RepoReader } from './detect-read';

export interface PhpRun {
  installCmd: string;
  startCmd: string | null;
  checkCmd: string | null;
  port: string | null;
}

/** `composer.json` → what a session runs. `configured` is a port the repository's environment already names. */
export async function describePhp(
  reader: RepoReader,
  files: Set<string>,
  configured: string | null,
  guessPort: (text: string) => string | null
): Promise<PhpRun> {
  const composer = await readJson(reader, 'composer.json');
  const scripts = record(composer?.scripts);
  const raw = (await reader.read('composer.json')) ?? '';
  // `--no-interaction` because nobody answers a prompt in a sandbox, and dists
  // rather than clones: a clone needs git access to every package's host.
  const installCmd = 'composer install --no-interaction --prefer-dist';

  // Composer stops a script after 300 seconds unless told otherwise, and a server is a script that never ends.
  const script = ['start', 'serve', 'dev'].find((one) => one in scripts);
  const gate = ['test', 'check', 'ci'].find((one) => one in scripts);
  const runner = /"pestphp\/pest"/.test(raw)
    ? 'vendor/bin/pest'
    : /"phpunit\/phpunit"/.test(raw) || files.has('phpunit.xml') || files.has('phpunit.xml.dist')
      ? 'vendor/bin/phpunit'
      : null;
  const checkCmd = gate ? `composer run-script ${gate}` : runner;

  const written = guessPort(Object.values(scripts).flat().join(' '));
  const at = Number((configured ?? written ?? ':8000').slice(1));
  if (script) {
    return {
      installCmd,
      startCmd: `COMPOSER_PROCESS_TIMEOUT=0 composer run-script ${script}`,
      checkCmd,
      port: configured ?? written ?? ':8000'
    };
  }
  if (files.has('artisan')) {
    return { installCmd, startCmd: `php artisan serve --host=0.0.0.0 --port=${at}`, checkCmd, port: `:${at}` };
  }
  const docroot = (await reader.read('public/index.php')) !== null ? 'public' : files.has('index.php') ? '.' : null;
  // No router script: the built-in server serves a file that exists and falls
  // back to the docroot's index.php for a path that does not, which is what a
  // front controller wants — and its assets still come back as files.
  if (docroot) return { installCmd, startCmd: `php -S 0.0.0.0:${at} -t ${docroot}`, checkCmd, port: `:${at}` };
  return { installCmd, startCmd: null, checkCmd, port: configured };
}

/** Composer writes a script as a string or as a list of them. */
function record(value: unknown): Record<string, string[]> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: Record<string, string[]> = {};
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    const lines = Array.isArray(entry) ? entry : [entry];
    out[key] = lines.filter((line): line is string => typeof line === 'string');
  }
  return out;
}
